import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi, departmentApi, bulkApi, planApi } from '../api';
import { Btn, Alert, Spinner, EmptyState, RoleBadge, Avatar } from '../components/ui';
import PlanLimitModal from '../components/PlanLimitModal';
import UsageIndicator from '../components/UsageIndicator';

const PAGE_SIZE = 15;

// ── Create/Edit modal ──────────────────────────────────────────────────────────
function EmployeeModal({ mode, employee, departments, onClose, onSaved, onPlanLimit }) {
  const isEdit = mode === 'edit';
  const [form,  setForm]  = useState(
    isEdit
      ? { name: employee.name, role: employee.role, departmentIds: employee.departments?.map(d => d.id) || [] }
      : { name: '', employeeId: '', role: 'EMPLOYEE', departmentIds: [] }
  );
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleDept = (id) => setForm(f => ({
    ...f,
    departmentIds: f.departmentIds.includes(id)
      ? f.departmentIds.filter(d => d !== id)
      : [...f.departmentIds, id],
  }));

  const validate = () => {
    if (!form.name.trim())                       return 'Name is required';
    if (!isEdit && !form.employeeId.trim())       return 'Employee ID is required';
    if (!isEdit && !/^[A-Za-z0-9_-]+$/.test(form.employeeId)) return 'Employee ID: only letters, numbers, hyphens, underscores';
    if (form.departmentIds.length === 0)          return 'At least one department is required';
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(''); setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await userApi.update(employee.id, {
          name: form.name,
          role: form.role,
          departmentIds: form.departmentIds,
        });
      } else {
        res = await userApi.create(form);
      }
      if (res.data.success) { onSaved(res.data.data); onClose(); }
    } catch (e) {
      if (e.response?.status === 402 && onPlanLimit) {
        onClose();                          // close create modal first
        onPlanLimit(e.response.data);       // show plan limit modal
      } else {
        setError(e.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} employee`);
      }
    } finally { setSaving(false); }
  };

  return (
    <div style={O.overlay}>
      <div style={O.modal}>
        <div style={O.header}>
          <h3 style={O.title}>{isEdit ? 'Edit Employee' : 'Add Employee'}</h3>
          <button onClick={onClose} style={O.close}>×</button>
        </div>
        {error && <Alert type="error" message={error} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '8px' }}>
          <Field label="Full Name *">
            <input value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. Rahul Sharma" style={O.input} />
          </Field>
          {!isEdit && (
            <Field label="Employee ID *">
              <input value={form.employeeId} onChange={e => set('employeeId', e.target.value)}
                placeholder="e.g. EMP001" style={O.input} />
            </Field>
          )}
          <Field label="Role">
            <select value={form.role} onChange={e => set('role', e.target.value)} style={O.input}>
              <option value="EMPLOYEE">Employee</option>
              <option value="ADMIN">Admin</option>
            </select>
          </Field>
          <Field label="Departments * (required)">
            {departments.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--color-danger)' }}>
                No departments exist yet. Create departments first before adding employees.
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                {departments.map(d => (
                  <button key={d.id} onClick={() => toggleDept(d.id)} style={{
                    padding: '6px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 500,
                    background: form.departmentIds.includes(d.id) ? 'var(--color-primary)' : 'var(--color-surface)',
                    color:      form.departmentIds.includes(d.id) ? '#fff' : 'var(--color-text-secondary)',
                    border:     `1.5px solid ${form.departmentIds.includes(d.id) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  }}>{d.name}</button>
                ))}
              </div>
            )}
            {form.departmentIds.length === 0 && departments.length > 0 && (
              <p style={{ fontSize: '12px', color: 'var(--color-danger)', marginTop: '4px' }}>
                Select at least one department
              </p>
            )}
          </Field>
          <div style={{ marginTop: '8px' }}>
            <Btn onClick={submit} loading={saving} fullWidth disabled={departments.length === 0}>
              {isEdit ? 'Save Changes' : 'Create Employee'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Bulk upload modal ──────────────────────────────────────────────────────────
function BulkUploadModal({ onClose, onDone }) {
  const [file,     setFile]     = useState(null);
  const [uploading,setUploading]= useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState('');

  const downloadTemplate = async () => {
    try {
      const res = await bulkApi.downloadTemplate();
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href    = url;
      a.download = 'tracern-employee-upload-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to download template');
    }
  };

  const upload = async () => {
    if (!file) { setError('Select a file first'); return; }
    setError(''); setUploading(true); setResult(null);
    try {
      const res = await bulkApi.upload(file);
      setResult(res.data.data);
      if (res.data.data.successCount > 0) onDone();
    } catch (e) {
      setError(e.response?.data?.message || 'Upload failed. Please check the file format.');
    } finally { setUploading(false); }
  };

  return (
    <div style={O.overlay}>
      <div style={{ ...O.modal, maxWidth: '560px' }}>
        <div style={O.header}>
          <h3 style={O.title}>Bulk Upload Employees</h3>
          <button onClick={onClose} style={O.close}>×</button>
        </div>

        {/* Step 1 */}
        <div style={B.step}>
          <div style={B.stepNum}>1</div>
          <div style={{ flex: 1 }}>
            <p style={B.stepTitle}>Download the template</p>
            <p style={B.stepDesc}>Get the Excel template with the correct structure and department reference list.</p>
            <button onClick={downloadTemplate} style={B.downloadBtn}>
              ↓ Download Template (.xlsx)
            </button>
          </div>
        </div>

        {/* Step 2 */}
        <div style={B.step}>
          <div style={B.stepNum}>2</div>
          <div style={{ flex: 1 }}>
            <p style={B.stepTitle}>Fill in employee details</p>
            <p style={B.stepDesc}>Add one employee per row. Department name must match exactly. Employee ID must be unique.</p>
          </div>
        </div>

        {/* Step 3 */}
        <div style={B.step}>
          <div style={B.stepNum}>3</div>
          <div style={{ flex: 1 }}>
            <p style={B.stepTitle}>Upload the filled file</p>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={e => { setFile(e.target.files[0]); setResult(null); setError(''); }}
                style={{ display: 'none' }}
                id="bulk-file-input"
              />
              <span style={B.fileBtn}>
                {file ? `✓ ${file.name}` : 'Choose Excel file (.xlsx)'}
              </span>
            </label>
            {error && <Alert type="error" message={error} />}
            <Btn onClick={upload} loading={uploading} disabled={!file} fullWidth>
              Upload & Create Employees
            </Btn>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div style={{ marginTop: '16px' }}>
            <div style={{
              padding: '14px 16px', borderRadius: 'var(--radius-md)',
              background: result.failureCount > 0 ? '#FFFBEB' : '#F0FFF4',
              border: `1px solid ${result.failureCount > 0 ? '#FAF089' : '#9AE6B4'}`,
            }}>
              <p style={{ fontWeight: 600, marginBottom: '4px', color: result.failureCount > 0 ? '#744210' : 'var(--color-success)' }}>
                {result.successCount} employee{result.successCount !== 1 ? 's' : ''} created successfully
                {result.failureCount > 0 && ` · ${result.failureCount} row${result.failureCount !== 1 ? 's' : ''} skipped`}
              </p>
              <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                Total rows processed: {result.totalRows}
              </p>
            </div>

            {result.errors?.length > 0 && (
              <div style={{ marginTop: '10px', maxHeight: '180px', overflowY: 'auto' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-danger)', marginBottom: '6px' }}>
                  Errors ({result.errors.length}):
                </p>
                {result.errors.map((err, i) => (
                  <div key={i} style={{ fontSize: '12px', color: 'var(--color-danger)', padding: '4px 8px', background: '#FFF5F5', borderRadius: 'var(--radius-sm)', marginBottom: '4px' }}>
                    {err}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Btn>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '6px' }}>{label}</label>
      {children}
    </div>
  );
}

// ── Main Employees page ────────────────────────────────────────────────────────
export default function Employees() {
  const navigate              = useNavigate();
  const [employees, setEmps]  = useState([]);
  const [depts,     setDepts] = useState([]);
  const [loading,   setLoad]  = useState(true);
  const [error,     setError] = useState('');
  const [search,    setSearch]= useState('');
  const [roleFilter,setRole]  = useState('ALL');
  const [page,      setPage]  = useState(1);
  const [usage,     setUsage] = useState(null);
  const [planError, setPlanError] = useState(null);

  // modals
  const [showCreate,   setShowCreate]   = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [showBulk,     setShowBulk]     = useState(false);

  const load = useCallback(() => {
    Promise.all([userApi.getAll(), departmentApi.getAll(), planApi.getUsage()])
      .then(([u, d, p]) => {
        setEmps(u.data.data || []);
        setDepts(d.data.data || []);
        setUsage(p.data.data);
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoad(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, roleFilter]);

  const filtered = employees.filter(e => {
    const matchSearch = !search ||
      e.name?.toLowerCase().includes(search.toLowerCase()) ||
      e.employeeId?.toLowerCase().includes(search.toLowerCase()) ||
      e.departments?.some(d => d.name.toLowerCase().includes(search.toLowerCase()));
    const matchRole = roleFilter === 'ALL' || e.role === roleFilter;
    return matchSearch && matchRole;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeCount   = employees.filter(e => e.active).length;
  const inactiveCount = employees.filter(e => !e.active).length;

  return (
    <div style={{ padding: '20px 16px', maxWidth: '760px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={S.title}>Employees</h1>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
            <p style={S.sub}>
              {activeCount} active
              {inactiveCount > 0 && <span style={{ color: 'var(--color-text-muted)' }}> · {inactiveCount} inactive</span>}
              {' · '}{filtered.length} shown
            </p>
            {usage && (
              <UsageIndicator
                current={usage.employees}
                max={usage.maxEmployees}
                label="Employee slots"
              />
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn variant="secondary" size="sm" onClick={() => setShowBulk(true)}>⬆ Bulk Upload</Btn>
          <Btn onClick={() => setShowCreate(true)}>+ Add Employee</Btn>
        </div>
      </div>

      {error && <Alert type="error" message={error} />}

      {/* No departments warning */}
      {depts.length === 0 && (
        <div style={{ padding: '12px 16px', background: '#FFFBEB', border: '1px solid #FAF089', borderRadius: 'var(--radius-md)', marginBottom: '14px', fontSize: '13px', color: '#744210' }}>
          ⚠️ No departments created yet.{' '}
          <button onClick={() => navigate('/departments')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontWeight: 600, fontSize: '13px' }}>
            Create departments first →
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '15px', color: '#9CA3AF', pointerEvents: 'none' }}>🔍</span>
          <input
            placeholder="Search by name, ID, or department…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={S.input}
          />
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['ALL', 'SUPER_ADMIN', 'ADMIN', 'EMPLOYEE'].map(r => (
            <button key={r} onClick={() => setRole(r)} style={{
              padding: '4px 10px', borderRadius: '999px', cursor: 'pointer',
              fontSize: '12px', fontWeight: roleFilter === r ? 600 : 400,
              background: roleFilter === r ? 'var(--color-primary)' : 'var(--color-surface)',
              color:      roleFilter === r ? '#fff' : 'var(--color-text-secondary)',
              border:     `1px solid ${roleFilter === r ? 'var(--color-primary)' : 'var(--color-border)'}`,
            }}>{r === 'ALL' ? 'All Roles' : r.replace('_', ' ')}</button>
          ))}
        </div>
      </div>

      {/* Employee list */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
      ) : paginated.length === 0 ? (
        <EmptyState icon="👤" title="No employees found"
          subtitle={search ? 'Try a different search.' : 'Add your first employee.'} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {paginated.map(emp => (
            <div key={emp.id} style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '14px 16px', background: '#fff',
              borderRadius: '14px',
              border: `1px solid ${emp.active ? '#F1F5F9' : '#E5E7EB'}`,
              opacity: emp.active ? 1 : 0.6,
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              transition: 'box-shadow 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'}
            >
              {/* Avatar */}
              <div
                onClick={() => navigate(`/employees/${emp.id}`)}
                style={{ flexShrink: 0, cursor: 'pointer' }}
              ><Avatar name={emp.name || '?'} size={42} /></div>

              {/* Info — tappable to profile */}
              <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate(`/employees/${emp.id}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <p style={{ fontWeight: 500, fontSize: '15px' }}>{emp.name}</p>
                  <RoleBadge role={emp.role} />
                  {!emp.active && <span style={{ fontSize: '11px', background: '#EDF2F7', color: '#718096', padding: '2px 8px', borderRadius: '999px' }}>Inactive</span>}
                  {emp.away    && <span style={{ fontSize: '11px', background: '#FFFBEB', color: '#D69E2E', padding: '2px 8px', borderRadius: '999px' }}>Away</span>}
                </div>
                <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                  {emp.employeeId} · {emp.departments?.map(d => d.name).join(', ') || '⚠️ No department'}
                </p>
              </div>

              {/* Edit button */}
              <button
                onClick={e => { e.stopPropagation(); setEditEmployee(emp); }}
                style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '6px 12px', fontSize: '13px', cursor: 'pointer', color: 'var(--color-text-secondary)', flexShrink: 0 }}
              >Edit</button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={pagination(page === 1)}
          >← Prev</button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i-1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => (
              p === '...'
                ? <span key={`ellipsis-${i}`} style={{ fontSize: '14px', color: 'var(--color-text-muted)', padding: '0 4px' }}>…</span>
                : <button key={p} onClick={() => setPage(p)} style={pagination(false, p === page)}>
                    {p}
                  </button>
            ))
          }

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={pagination(page === totalPages)}
          >Next →</button>

          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginLeft: '8px' }}>
            Page {page} of {totalPages} · {filtered.length} total
          </span>
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <EmployeeModal
          mode="create"
          departments={depts}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
          onPlanLimit={setPlanError}
        />
      )}
      {editEmployee && (
        <EmployeeModal
          mode="edit"
          employee={editEmployee}
          departments={depts}
          onClose={() => setEditEmployee(null)}
          onSaved={() => { setEditEmployee(null); load(); }}
          onPlanLimit={setPlanError}
        />
      )}
      {showBulk && (
        <BulkUploadModal
          onClose={() => setShowBulk(false)}
          onDone={load}
        />
      )}

      {/* Plan limit modal — shown on 402 from any create operation */}
      {planError && (
        <PlanLimitModal
          error={planError}
          onClose={() => setPlanError(null)}
        />
      )}
    </div>
  );
}

const pagination = (disabled, active = false) => ({
  padding: '6px 12px', borderRadius: 'var(--radius-sm)', cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '13px', fontWeight: active ? 700 : 400,
  background: active ? 'var(--color-primary)' : 'var(--color-surface)',
  color:      active ? '#fff' : disabled ? 'var(--color-text-muted)' : 'var(--color-text)',
  border:     `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
  opacity:    disabled ? 0.5 : 1,
});

const S = {
  title: { fontSize: '22px', fontWeight: 800, color: '#111827', marginBottom: '2px', letterSpacing: '-0.04em', fontFamily: "'Sora', sans-serif" },
  sub:   { fontSize: '13px', color: '#6B7280' },
  input: { padding: '11px 14px 11px 42px', borderRadius: '12px', border: '1.5px solid #E5E7EB', fontSize: '14px', width: '100%', background: '#F9FAFB', color: '#111827', outline: 'none' },
};
const O = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200, padding: '16px' },
  modal:   { background: '#fff', borderRadius: '20px 20px 16px 16px', padding: '24px', width: '100%', maxWidth: '480px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 -4px 40px rgba(0,0,0,0.15)', animation: 'slideUp 0.2s ease' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  title:   { fontSize: '18px', fontWeight: 700, color: '#111827', letterSpacing: '-0.03em', fontFamily: "'Sora', sans-serif" },
  close:   { background: '#F1F5F9', border: 'none', width: '30px', height: '30px', borderRadius: '50%', fontSize: '18px', cursor: 'pointer', color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  input:   { width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1.5px solid #E5E7EB', fontSize: '14px', color: '#111827', background: '#F9FAFB', outline: 'none' },
};
const B = {
  step:      { display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '14px 0', borderBottom: '1px solid var(--color-border)' },
  stepNum:   { width: '28px', height: '28px', borderRadius: '50%', background: 'var(--color-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, flexShrink: 0 },
  stepTitle: { fontWeight: 600, fontSize: '14px', marginBottom: '4px' },
  stepDesc:  { fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.5, marginBottom: '8px' },
  downloadBtn:{ display: 'inline-block', padding: '8px 16px', background: 'var(--color-primary-lighter)', color: 'var(--color-primary)', borderRadius: 'var(--radius-sm)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: '1px solid var(--color-primary)' },
  fileBtn:   { display: 'inline-block', padding: '8px 16px', background: 'var(--color-bg)', border: '1.5px dashed var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: '13px', cursor: 'pointer', marginBottom: '10px', width: '100%', textAlign: 'center', color: 'var(--color-text-secondary)' },
};
